import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

export default function OverviewIdentifiedModal({
  isOpen,
  onClose,
  title,
  sections,
  emptyMessage,
}) {
  const [isAnimating, setIsAnimating] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (isOpen) setIsAnimating(true);

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    setIsAnimating(false);
    closeTimerRef.current = setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const hasData = sections?.some((section) => section.items.length > 0);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
      onClick={handleClose}
    >
      <div
        className={`bg-background-secondary border-t border-border-secondary rounded-tl-[24px] rounded-tr-[24px] w-full h-[85vh] max-h-[85vh] lg:w-[80%] lg:h-[65vh] relative overflow-hidden flex flex-col transition-transform duration-300 ease-out ${
          isAnimating ? "translate-y-0" : "translate-y-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-[10px] pb-[2px] flex-shrink-0">
          <div className="h-[4px] w-[36px] rounded-full bg-border-dark" />
        </div>

        <div className="box-border flex items-center justify-between px-4 py-[8px] border-b border-border-secondary flex-shrink-0">
          <div className="box-border content-center flex flex-wrap gap-[8px] items-center px-0 py-[10px] relative w-full">
            <div
              className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-text-primary text-base text-nowrap tracking-[-0.16px]"
              style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
            >
              <p className="leading-[normal] whitespace-pre">{title}</p>
            </div>
          </div>
          <button
            className="block cursor-pointer overflow-clip relative shrink-0 p-3 -m-3"
            onClick={handleClose}
          >
            <X size={24} className="text-black" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {hasData ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {sections.map((section) => (
                <div key={section.title} className="flex flex-col gap-2">
                  {/* A heading, not another row. This used to be set in the
                      same semibold 16px as the part names directly beneath it,
                      differing only in colour token, so the two lists read as
                      one undifferentiated column. Smaller, upper-cased,
                      letter-spaced, with the count in a pill — now that each
                      item below is its own card rather than a hairline-ruled
                      row, the heading no longer needs a rule of its own to
                      read as a separate group.

                      Kept at full-strength ink rather than a muted grey: the
                      muted tokens measure 2.96:1 (tertiary) and 3.68:1
                      (secondary) against this surface, both under the 4.5:1
                      small text needs, and 11px is exactly where that bites.
                      Size, case and tracking carry the distinction instead —
                      which is the sturdier way to draw it anyway. */}
                  <div className="flex items-center justify-between gap-2 px-[8px] pb-[8px]">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-primary">
                      {section.title}
                    </span>
                    {section.items.length > 0 && (
                      <span className="rounded-full bg-background-primary px-[8px] py-[2px] text-[11px] font-semibold tabular-nums text-text-primary">
                        {section.items.length}
                      </span>
                    )}
                  </div>
                  {section.items.length > 0 ? (
                    <div className="flex flex-col gap-[6px]">
                      {section.items.map((item) => (
                        <div
                          key={item.label}
                          className="box-border content-stretch flex gap-[12px] items-center overflow-visible rounded-[12px] bg-background-primary px-[12px] py-[10px] relative shrink-0 w-full"
                        >
                          {(item.icon || section.icon) && (
                            <div className="flex size-[32px] shrink-0 items-center justify-center rounded-full bg-background-secondary">
                              <img
                                alt=""
                                className="block max-w-none size-[16px]"
                                src={item.icon || section.icon}
                              />
                            </div>
                          )}
                          <div
                            className="flex flex-col font-['Noto_Sans:SemiBold',_sans-serif] font-semibold justify-end leading-[0] relative shrink-0 text-base text-black tracking-[-0.16px]"
                            style={{
                              fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                            }}
                          >
                            <p className="leading-[normal]">{item.label}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-text-tertiary text-sm">
                      {section.emptyMessage}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-text-tertiary text-sm">{emptyMessage}</div>
          )}
        </div>
      </div>
    </div>
  );
}
